import { useCallback, useMemo } from 'react';
import { debounce } from 'throttle-debounce';

import { PaginationWithScroll } from '@ui/components/PaginationWithScroll/PaginationWithScroll';

import { useRoundShortGamesSocket } from '@features/shared/api/streams';
import {
  useTournamentRoundMovementPrivateQuery,
  useTournamentResultsQuery,
  useTournamentRoundMovementTeamQuery,
} from '@features/tournament/api/hooks';
import { useGoToGame } from '@features/tournament/hooks/useGoToGame';
import { useTournamentCtx } from '@features/tournament/providers/TournamentProvider';
import { RoundMovement } from '@features/tournament/ui/components/RoundMovement/RoundMovement';
import { RoundMovementSkeleton } from '@features/tournament/ui/components/RoundMovement/RoundMovementSkeleton';
import { TournamentKnockoutResults } from '@features/tournament/ui/components/TournamentKnockoutResults';
import { PlayerShieldQuickPeekWidget } from '@features/user/ui/PlayerShieldQuickPeek/PlayerShieldQuickPeekWidget';

import styles from './RoundMovement.module.css';

const REFETCH_DEBOUNCE_MS = 3 * 1000;

export function RoundMovementWidget() {
  const { state, selectRound } = useTournamentCtx();
  const { tournament, clubId, tournamentId, visibleRounds, selectedRound, lastRound } = state;

  const roundNumber = selectedRound?.roundNumber;

  const isSeance = tournament?.system === 'Seance';
  const isKnockout = tournament?.system === 'Knockout';
  const isResultsQuery = isSeance || isKnockout;

  const totalRoundsCount = visibleRounds?.length || 1;

  const isRoundMovementEnabled = !!roundNumber && !isResultsQuery;

  const is2x2Mode = tournament?.mode === 'TwoOnTwo';

  // Сеансы используют ручку results
  const isTeam = tournament?.mode === 'Team' || is2x2Mode;

  const {
    data: privateMovementData,
    isPending: isPrivateMovementPending,
    refetch: privateGamesRefetch,
  } = useTournamentRoundMovementPrivateQuery(
    clubId,
    tournamentId,
    roundNumber || 1,
    isRoundMovementEnabled && !isTeam,
  );

  const {
    data: teamMovementData,
    isPending: isTeamMovementPending,
    refetch: teamGamesRefetch,
  } = useTournamentRoundMovementTeamQuery(
    clubId,
    tournamentId,
    roundNumber || 1,
    isRoundMovementEnabled && isTeam,
  );

  const movementData = isTeam ? teamMovementData : privateMovementData;
  const isMovementPending = isTeam ? isTeamMovementPending : isPrivateMovementPending;
  const gamesRefetch = isTeam ? teamGamesRefetch : privateGamesRefetch;

  const {
    data: resultsData,
    isPending: isResultsPending,
    refetch: resultsRefetch,
  } = useTournamentResultsQuery(clubId, tournamentId, isResultsQuery);

  const rows = isSeance ? (resultsData?.seanceResults?.rows ?? []) : (movementData?.rows ?? []);

  const refetch = useMemo(
    () =>
      debounce(REFETCH_DEBOUNCE_MS, () => {
        if (isResultsQuery) {
          resultsRefetch();
        } else {
          gamesRefetch();
        }
      }),
    [gamesRefetch, isResultsQuery, resultsRefetch],
  );

  // Отслеживание изменений игр
  useRoundShortGamesSocket({
    tournamentId,
    roundNumber: lastRound?.roundNumber ?? NaN,
    enabled: lastRound?.status === 'InProgress',
    cb: (e) => {
      if (e.eventType === 'GameChangedShort') {
        const result = e.currentState.result;
        if (result === 'Draw' || result === 'BlackWin' || result === 'WhiteWin') {
          refetch();
        }
      } else if (e.eventType === 'NewGameStarted') {
        refetch();
      }
    },
  });

  const goToGame = useGoToGame(clubId);
  const handleGoToGame = useCallback(
    (gameId: string) => {
      goToGame(gameId, tournamentId);
    },
    [tournamentId, goToGame],
  );

  const handleSelectRoundById = useCallback(
    (id: string) => {
      const r = visibleRounds?.find((r) => r.roundId === id);
      if (r) {
        selectRound(r.roundNumber);
      }
    },
    [selectRound, visibleRounds],
  );

  if (isKnockout) {
    const results = resultsData?.knockOutResults;
    if (!results) return null;
    return (
      <TournamentKnockoutResults
        rounds={results.rounds}
        selectedRoundId={selectedRound?.roundId}
        onSelectRound={handleSelectRoundById}
        onGameClick={handleGoToGame}
        renderShieldQuickPeek={(userId) => <PlayerShieldQuickPeekWidget userId={userId} />}
      />
    );
  }

  const showSkeleton = (isSeance ? isResultsPending : isMovementPending) && rows.length === 0;

  return (
    <div className={styles.widget}>
      {(tournament?.rounds.length ?? 0) > 1 && (
        <PaginationWithScroll
          className={styles.rounds}
          label="Тур"
          variant="new"
          pageTotal={totalRoundsCount}
          pageNum={roundNumber || 1}
          onPage={selectRound}
        />
      )}
      {showSkeleton ? (
        <RoundMovementSkeleton
          rowCount={Math.floor(
            (isTeam ? (tournament?.teamsCount ?? 10) : (tournament?.participantsCount ?? 10)) / 2,
          )}
          isTeam={isTeam}
        />
      ) : (
        <RoundMovement
          rows={rows}
          total={resultsData?.seanceResults?.total}
          roundNumber={roundNumber}
          is2x2Mode={is2x2Mode}
          onGameClick={handleGoToGame}
          renderQuickPeekShield={(userId) => <PlayerShieldQuickPeekWidget userId={userId} />}
        />
      )}
    </div>
  );
}
